import { EMessageRole, type IMessage } from "../types/chat";
import type { TSkillActivationRecord } from "../types/skills";
import type { IMergeSkillActivationsProps } from "./skill-activations.types";

// NOTE: NSAI-3308 — merge live `skillActivations` records into what a message
// already carries. Deduplication is by `skillUuid`, which is what makes the
// timeline show ONE entry per skill in every replay case: a pinned skill that
// the model later requests again, a socket event redelivered after a reconnect,
// and a history refetch that arrives after the live event. A later record for the
// same skill wins, so a refetch carrying the durable row replaces the streamed
// copy of it rather than appending a twin.
export const mergeSkillActivations = ({
  existing,
  incoming,
}: IMergeSkillActivationsProps): TSkillActivationRecord[] => {
  const bySkillUuid = new Map<string, TSkillActivationRecord>();
  for (const record of existing ?? []) {
    bySkillUuid.set(record.skillUuid, record);
  }
  for (const record of incoming) {
    bySkillUuid.set(record.skillUuid, record);
  }

  // NOTE: Ordering comes from the server's `position`, never from arrival order,
  // so the same message reads the same way live and after a refresh.
  return [...bySkillUuid.values()].sort(
    (left, right) => left.position - right.position,
  );
};

// NOTE: The persistence-confirmation signal. A `newMessages` frame carries the
// persisted user message alongside the assistant placeholders, and only that user
// row proves the send reached the database — the Socket.IO acknowledgement proves
// nothing but delivery to the gateway. Returns the client request id the composer
// compares against its pending send.
export const findPersistedUserRequestId = (
  messages: readonly IMessage[],
): string | null => {
  for (const message of messages) {
    if (message.role !== EMessageRole.USER) {
      continue;
    }
    if (typeof message.clientRequestId === "string") {
      return message.clientRequestId;
    }
  }
  return null;
};

// NOTE: The ids a manual resend must replay: the ones the server normalized and
// stored on the original user message, not whatever the composer holds now.
export const getPersistedSkillSelection = (
  message: IMessage | null | undefined,
): string[] => {
  if (!message || !Array.isArray(message.requestedSkillUuids)) {
    return [];
  }
  return [...message.requestedSkillUuids];
};

// NOTE: NSAI-5925 — the refusal record a rendered user message states. Read
// through one accessor for the same reason `getPersistedSkillSelection` is: web
// and mobile must agree on the field and on what an absent one means, and an
// older row (or a message from before this ticket) simply carries nothing.
export const getRefusedSkillNames = (
  message: IMessage | null | undefined,
): string[] => {
  if (!message || !Array.isArray(message.refusedSkillNames)) {
    return [];
  }
  return message.refusedSkillNames.filter(
    (name): name is string => typeof name === "string" && name.length > 0,
  );
};
